import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MDBox from '../components/MDBox'; // Adjusted to src/components/
import MDTypography from '../components/MDTypography'; // Adjusted to src/components/
import { useTheme } from '@mui/material/styles';
import { usePeriod } from '../components/PeriodContext'; // Adjusted to src/components/
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { db, collection, query, getDocs } from '../firebaseConfig'; // Moved to src/

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNumber;
};

const TeamTotalTradesSummary = ({ userID, tradeAccountID }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [trades, setTrades] = useState([]);
  const [totalTrades, setTotalTrades] = useState(0);
  const [totalWins, setTotalWins] = useState(0);
  const [totalLosses, setTotalLosses] = useState(0);
  const [totalBreakeven, setTotalBreakeven] = useState(0);
  const [totalMissedTrades, setTotalMissedTrades] = useState(0);
  const [totalMissedWins, setTotalMissedWins] = useState(0);
  const [totalMissedLosses, setTotalMissedLosses] = useState(0);
  const [longTrades, setLongTrades] = useState({ wins: 0, total: 0 });
  const [shortTrades, setShortTrades] = useState({ wins: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true);
        const tradesQuery = query(collection(db, `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`));
        const tradesDocs = await getDocs(tradesQuery);
        const tradesData = tradesDocs.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date ? new Date(doc.data().date) : new Date(),
        }));
        console.log('TeamTotalTradesSummary: Fetched trades for userID:', userID, 'tradeAccountID:', tradeAccountID, tradesData);
        setTrades(tradesData);
      } catch (error) {
        console.error('TeamTotalTradesSummary: Error fetching trades:', error);
        setTrades([]);
      } finally {
        setLoading(false);
      }
    };

    if (userID && tradeAccountID) {
      fetchTrades();
    }
  }, [userID, tradeAccountID]);

  useEffect(() => {
    console.log('TeamTotalTradesSummary: Processing trades with period:', selectedPeriod, selectedYear, selectedMonth, 'showMissedTrades:', showMissedTrades, 'selectedTag:', selectedTag);

    const filterTrades = () => {
      let wins = 0;
      let losses = 0;
      let breakevens = 0;
      let missedWins = 0;
      let missedLosses = 0;
      let longStats = { wins: 0, total: 0 };
      let shortStats = { wins: 0, total: 0 };

      trades.forEach((entry) => {
        const entryDate = new Date(entry.date);

        const matchesPeriod = () => {
          if (selectedPeriod === 'today') {
            return entryDate.toDateString() === new Date().toDateString();
          } else if (selectedPeriod === 'this-week') {
            return entryDate.getWeek() === new Date().getWeek() && entryDate.getFullYear() === new Date().getFullYear();
          } else if (selectedPeriod === 'this-month') {
            return entryDate.getMonth() === new Date().getMonth() && entryDate.getFullYear() === new Date().getFullYear();
          } else if (selectedPeriod === 'this-year') {
            return entryDate.getFullYear() === new Date().getFullYear();
          } else if (selectedPeriod === 'pick-month') {
            return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
          } else if (selectedPeriod === 'pick-year') {
            return entryDate.getFullYear() === selectedYear;
          } else if (selectedPeriod === 'pick-month-year') {
            return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
          } else {
            return true; // all-time
          }
        };

        const matchesTag = selectedTag === 'all' || 
          (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));

        if (matchesPeriod() && matchesTag) {
          if (!isNaN(entry.returns)) {
            if (entry.shortLong === 'Long') {
              longStats.total++;
              if (entry.returns > 0) longStats.wins++;
            } else if (entry.shortLong === 'Short') {
              shortStats.total++;
              if (entry.returns > 0) shortStats.wins++;
            }

            if (entry.status === 'Closed') {
              if (entry.returns > 0) {
                wins++;
              } else if (entry.returns < 0) {
                losses++;
              } else {
                breakevens++;
              }
            } else if (entry.status === 'Missed') {
              entry.returns > 0 ? missedWins++ : missedLosses++;
            }
          }
        }
      });

      const tradesCount = wins + losses + breakevens;
      const missedTradesCount = missedWins + missedLosses;

      setTotalTrades(tradesCount);
      setTotalWins(wins);
      setTotalLosses(losses);
      setTotalBreakeven(breakevens);
      setTotalMissedTrades(missedTradesCount);
      setTotalMissedWins(missedWins);
      setTotalMissedLosses(missedLosses);
      setLongTrades(longStats);
      setShortTrades(shortStats);
    };

    filterTrades();
  }, [trades, selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag]);

  const tooltipContent = `The Trade Summary shows all trades based on the Period Data Controller settings. When viewing closed trades (default), it includes wins, losses, breakeven, and missed trades. When toggled to missed trades, it focuses only on missed opportunities. Long/Short win rates help identify the trader's strongest direction.`;

  const getWinRateColor = (winRate) => {
    return winRate >= 50 ? 'success' : 'error';
  };

  if (loading) {
    return (
      <Card style={{ minHeight: '260px' }}>
        <MDBox display='flex' justifyContent='center' alignItems='center' height='100%'>
          <MDTypography variant='caption' color='text'>
            Loading trades...
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  return (
    <Card style={{ minHeight: '260px' }}>
      <MDBox display='flex' flexDirection='column' justifyContent='space-between' pt={1} px={2} height='100%'>
        <MDBox display='flex' justifyContent='space-between'>
          <MDBox
            variant='gradient'
            bgColor='light'
            color='dark'
            coloredShadow='light'
            borderRadius='xl'
            display='flex'
            justifyContent='center'
            alignItems='center'
            width='4rem'
            height='4rem'
            mt={-3}
          >
            <AccountBalanceIcon fontSize='medium' color='inherit' />
          </MDBox>
          <MDBox textAlign='right' lineHeight={1.25}>
            <MDTypography variant='button' fontWeight='light' color='text' fontFamily='inherit'>
              Total Trades Summary {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement='bottom-end'
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    },
                  },
                },
              }}
            >
              <InfoIcon
                sx={{
                  ml: 1,
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help',
                }}
              />
            </Tooltip>
            <MDTypography variant='h4' fontFamily='inherit'>
              {showMissedTrades ? totalMissedTrades : totalTrades}
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        <MDBox pb={2} px={2}>
          {!showMissedTrades && (
            <>
              <MDTypography component='p' variant='button' color='text' display='flex' fontFamily='inherit'>
                Wins:{' '}
                <MDTypography component='span' variant='button' fontWeight='bold' color='success' fontFamily='inherit'>
                  {totalWins}
                </MDTypography>
                {' │ '}
                Losses:{' '}
                <MDTypography component='span' variant='button' fontWeight='bold' color='error' fontFamily='inherit'>
                  {totalLosses}
                </MDTypography>
              </MDTypography>
              <MDTypography component='p' variant='button' color='text' display='flex' fontFamily='inherit'>
                Breakeven:{' '}
                <MDTypography component='span' variant='button' fontWeight='bold' color='warning' fontFamily='inherit'>
                  {totalBreakeven}
                </MDTypography>
              </MDTypography>
            </>
          )}
          <MDTypography component='p' variant='button' color='text' display='flex' fontFamily='inherit'>
            Total Missed (M):{' '}
            <MDTypography component='span' variant='button' fontWeight='bold' color='text' fontFamily='inherit'>
              {totalMissedTrades}
            </MDTypography>
          </MDTypography>
          <MDTypography component='p' variant='button' color='text' display='flex' fontFamily='inherit'>
            (M) Wins:{' '}
            <MDTypography component='span' variant='button' fontWeight='bold' color='success' fontFamily='inherit'>
              {totalMissedWins}
            </MDTypography>
            {' │ '}
            (M) Losses:{' '}
            <MDTypography component='span' variant='button' fontWeight='bold' color='error' fontFamily='inherit'>
              {totalMissedLosses}
            </MDTypography>
          </MDTypography>
          <MDTypography component='p' variant='button' color='text' display='flex' fontFamily='inherit'>
            Long Win Rate:{' '}
            <MDTypography
              component='span'
              variant='button'
              fontWeight='bold'
              color={longTrades.total > 0 ? getWinRateColor((longTrades.wins / longTrades.total) * 100) : 'text'}
              fontFamily='inherit'
            >
              {longTrades.total > 0 ? `${((longTrades.wins / longTrades.total) * 100).toFixed(1)}% (${longTrades.wins}/${longTrades.total})` : 'N/A'}
            </MDTypography>
          </MDTypography>
          <MDTypography component='p' variant='button' color='text' display='flex' fontFamily='inherit'>
            Short Win Rate:{' '}
            <MDTypography
              component='span'
              variant='button'
              fontWeight='bold'
              color={shortTrades.total > 0 ? getWinRateColor((shortTrades.wins / shortTrades.total) * 100) : 'text'}
              fontFamily='inherit'
            >
              {shortTrades.total > 0 ? `${((shortTrades.wins / shortTrades.total) * 100).toFixed(1)}% (${shortTrades.wins}/${shortTrades.total})` : 'N/A'}
            </MDTypography>
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
};

TeamTotalTradesSummary.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string.isRequired,
};

export default TeamTotalTradesSummary;